// KPI tiles (JEV ui.component_recipe: core.kpi_tile). The value is ink in
// tabular mono at --text-3, never coloured, never counted up. The delta is a
// sentence with a direction word, so its meaning never rests on colour or on
// the arrow alone.
import { Children, type ReactNode } from "react";
import { motion } from "motion/react";
import { ConsoleGlyph } from "./icons";
import { useEnterMotion } from "./motion";
import { formatNumber } from "./format";

export type DeltaTone = "positive" | "negative" | "neutral";

export interface StatDelta {
  /** Signed change, e.g. 12 or -4. Its sign picks the arrow. */
  value: number;
  /** Unit after the number, e.g. "%" or " orang". Default "%". */
  unit?: string;
  /** Whether this change is good news. Default: up is positive. */
  tone?: DeltaTone;
  /** What it is compared with, e.g. "dari periode lalu". */
  context: string;
}

export interface StatTileProps {
  label: string;
  /** A number (formatted id-ID) or a preformatted string. */
  value: number | string;
  /** Small unit after the value, e.g. "%". */
  unit?: string;
  delta?: StatDelta;
  /** A Sparkline, pinned to the tile bottom so tiles in a row share one baseline. */
  trend?: ReactNode;
  /** One badge under the label: a VerdictBadge, a Badge. Never a bare colour. */
  status?: ReactNode;
  /** One short line when the number needs a caveat, e.g. why it is missing. */
  note?: ReactNode;
}

function deltaWord(value: number): string {
  return value > 0 ? "naik" : "turun";
}

export function StatTile({ label, value, unit, delta, trend, status, note }: StatTileProps) {
  const shown = typeof value === "number" ? formatNumber(value) : value;
  const tone: DeltaTone = delta ? (delta.tone ?? (delta.value > 0 ? "positive" : delta.value < 0 ? "negative" : "neutral")) : "neutral";
  return (
    <div className="con-stat">
      <p className="con-stat-label">{label}</p>
      <p className="con-stat-value">
        {shown}
        {unit ? <span className="con-stat-unit">{unit}</span> : null}
      </p>
      {status ? <p className="con-stat-status">{status}</p> : null}
      {note ? <p className="con-stat-note">{note}</p> : null}
      {delta ? (
        <p className="con-stat-delta" data-tone={tone}>
          {delta.value !== 0 ? <ConsoleGlyph name={delta.value > 0 ? "up" : "down"} size={16} /> : null}
          <span>
            {delta.value === 0
              ? `tidak berubah ${delta.context}`
              : `${deltaWord(delta.value)} ${formatNumber(Math.abs(delta.value))}${delta.unit ?? "%"} ${delta.context}`}
          </span>
        </p>
      ) : null}
      {trend ? <div className="con-stat-trend">{trend}</div> : null}
    </div>
  );
}

export interface StatTileRowProps {
  /** Accessible name for the group, e.g. "Ringkasan corong pendaftaran". */
  label: string;
  /** 2, 3, or 4 StatTiles. The grid never leaves an orphan tile. */
  children: ReactNode;
}

/**
 * One grouped surface with hairlines between tiles.
 * 2 or 4 tiles: 2 across from 0, 4 across from 1024. 3 tiles: 1 column, 3 across from 640.
 */
export function StatTileRow({ label, children }: StatTileRowProps) {
  const enter = useEnterMotion();
  const count = Children.toArray(children).length;
  return (
    <motion.div className="con-stats" role="group" aria-label={label} data-count={count} {...enter}>
      {children}
    </motion.div>
  );
}

export interface StatGridProps {
  /** Accessible name for the group, e.g. "Metrik tahap Akuisisi". */
  label: string;
  /** Bare StatTiles inside a Panel: no grouped surface, one gap, top aligned. */
  children: ReactNode;
}

/**
 * Many small figures inside one Panel or one Bento cell: two columns from
 * 320px, four when the cell runs the full Bento width. A last row that would
 * be short stretches its own tiles, so the grid never ends on a gap.
 */
export function StatGrid({ label, children }: StatGridProps) {
  return (
    <div className="con-stat-grid" role="group" aria-label={label}>
      {children}
    </div>
  );
}

export interface StatCardProps extends StatTileProps {}

/**
 * One figure as its own card, for a Bento cell: the panel's chrome (surface,
 * one hairline on all four sides, the panel radius and inset) around a tile.
 */
export function StatCard(props: StatCardProps) {
  const enter = useEnterMotion();
  return (
    <motion.div className="con-stat-card" {...enter}>
      <StatTile {...props} />
    </motion.div>
  );
}
